import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  StepEquipamentoSchema,
  type StepEquipamentoFormValues,
} from '@/features/quotes/schemas/rd-equipamentos/step-equipamento.schema';
import { brlInputToCents } from '@/features/quotes/utils/rd-units.util';
import {
  buildPayload,
  type CoverageRow,
} from '@/features/quotes/components/rd-equipamentos/steps/sections/coverage-rows.util';
import { rdEquipamentosService } from '@/services/quotes/rd-equipamentos';
import type {
  RdEquipmentPayload,
  RdProposal,
} from '@/services/quotes/rd-equipamentos/types';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';
import { refetchRdProposalAndQuote } from './refetch-rd-proposal-and-quote.util';

/**
 * O equipamento é criado pelo POST e as coberturas gravadas por um PUT
 * endereçado ao `itemId` que só existe depois dele. Se o segundo passo
 * falhar, o item permanece na proposta sem as coberturas escolhidas —
 * desfazer seria pior (apagaria um cadastro válido), então a mensagem
 * aponta o caminho de conserto: a matriz dentro do card do item.
 */
const COVERAGE_RECOVERY_HINT =
  'Equipamento adicionado, mas as coberturas não foram salvas. Ajuste-as no card do equipamento.';

type FieldMetaLike = { errorMap?: Record<string, unknown> };

/**
 * Superfície mínima de `useAppForm` (TanStack Form) usada por este hook —
 * mesma técnica de `use-submit-rd-cliente.hook.ts`/
 * `use-submit-capital-global-step1.hook.ts`: evita tipar os ~20 parâmetros
 * genéricos de `FormApi`.
 */
/* eslint-disable no-unused-vars -- Numa ASSINATURA de tipo os nomes dos
   parâmetros não são variáveis: documentam o contrato de quem implementa
   (é o `setFieldMeta` do TanStack Form). A regra os lê como declarações
   não usadas; o desligamento vale só para este bloco de tipos. */
type AddEquipmentForm = {
  state: { values: StepEquipamentoFormValues };
  /**
   * Retorna `undefined` para campos ainda não montados (ex.: `nfNumber`/
   * `nfDate`/`nfIssuer` enquanto `isZeroKm` está desmarcado) — usado para
   * nunca chamar `setFieldMeta` num campo desmontado (ver uso abaixo).
   */
  getFieldMeta: (
    field: keyof StepEquipamentoFormValues
  ) => FieldMetaLike | undefined;
  setFieldMeta: (
    field: keyof StepEquipamentoFormValues,
    updater: (meta: FieldMetaLike | undefined) => FieldMetaLike
  ) => void;
  reset: () => void;
};
/* eslint-enable no-unused-vars */

type UseAddRdEquipmentParams = {
  proposalId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ver nota em `form` acima (`AddEquipmentForm`)
  form: any;
  /** Chamado após reset do formulário no sucesso — limpar estado local (busca/seleção do combobox). */
  onAdded?: () => void;
};

const FORM_FIELDS: (keyof StepEquipamentoFormValues)[] = [
  'equipmentCode',
  'year',
  'value',
  'brand',
  'model',
  'serialNumber',
  'isZeroKm',
  'nfNumber',
  'nfDate',
  'nfIssuer',
];

function clearSubmitErrors(form: AddEquipmentForm): void {
  FORM_FIELDS.forEach(field => {
    // `nfNumber`/`nfDate`/`nfIssuer` só montam quando "Zero km" está
    // marcado — para qualquer outro campo o form nunca registrou meta
    // (`getFieldMeta` retorna undefined), e `setFieldMeta` chamaria o
    // updater com `meta === undefined`, quebrando TODO submit (mesmo os
    // de equipamentos não-zero-km, o caso mais comum). Só limpa campos já
    // montados.
    if (form.getFieldMeta(field) === undefined) return;
    form.setFieldMeta(field, meta => ({
      ...meta,
      errorMap: { ...meta?.errorMap, onSubmit: undefined },
    }));
  });
}

/**
 * Orquestra o formulário "Adicionar equipamento" do Step 2 (RD Equipamentos,
 * fluxo individual): valida via `StepEquipamentoSchema` (após normalizar
 * ano/valor), chama `addEquipment` e, no sucesso, reseta o formulário e
 * refetcha `['rd-proposal', proposalId]` para o card novo aparecer na lista.
 *
 * Erros de negócio (400 com `detail`, sem `errors` de campo — ex.:
 * "Modalidade exige CEP/endereço do risco.") não têm um campo de formulário
 * para apontar, então além do toast (`toaster.create`) o hook expõe
 * `submitError` para o step renderizar um `Alert.Root status='error'`
 * persistente (o toast some em 5s; o alerta fica até o próximo submit).
 */
export function useAddRdEquipment({
  proposalId,
  form: formRaw,
  onAdded,
}: UseAddRdEquipmentParams) {
  const form = formRaw as AddEquipmentForm;
  const queryClient = useQueryClient();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { mutateAsync, isPending } = useMutation<
    RdProposal,
    Error,
    RdEquipmentPayload
  >({
    mutationKey: ['quotes', 'rd-equipamentos', 'add-equipment', proposalId],
    mutationFn: async (payload: RdEquipmentPayload) => {
      const response = await rdEquipamentosService.addEquipment(
        proposalId,
        payload
      );
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao adicionar equipamento');
      }

      return response.data;
    },
  });

  async function handleSubmit({
    coverageRows = [],
    existingItemIds = [],
  }: {
    coverageRows?: CoverageRow[];
    existingItemIds?: string[];
  } = {}) {
    setSubmitAttempted(true);
    setSubmitError(null);
    clearSubmitErrors(form);

    if (!proposalId) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description:
          'Proposta não encontrada. Recarregue a página e tente novamente.',
      });
      return;
    }

    const values = form.state.values;
    const parsed = StepEquipamentoSchema.safeParse({
      equipmentCode: values.equipmentCode,
      year: Number(values.year),
      value: brlInputToCents(values.value),
      brand: values.brand,
      model: values.model,
      // Obrigatório: passa a string crua (default '') para o schema validar o
      // vazio com a mensagem própria — `|| undefined` cairia no erro genérico
      // "Required" do Zod em vez de "Informe o chassi / nº de série".
      serialNumber: values.serialNumber,
      isZeroKm: values.isZeroKm,
      nfNumber: values.nfNumber || undefined,
      nfDate: values.nfDate || undefined,
      nfIssuer: values.nfIssuer || undefined,
      riskCep: values.riskCep || undefined,
      riskAddress: values.riskAddress || undefined,
      riskNumber: values.riskNumber || undefined,
      riskComplement: values.riskComplement || undefined,
      riskDistrict: values.riskDistrict || undefined,
      riskCity: values.riskCity || undefined,
      riskState: values.riskState || undefined,
    });

    if (!parsed.success) {
      parsed.error.issues.forEach(issue => {
        const field = issue.path[0] as keyof StepEquipamentoFormValues;
        form.setFieldMeta(field, meta => ({
          ...meta,
          errorMap: { ...meta?.errorMap, onSubmit: issue.message },
        }));
      });
      return;
    }

    try {
      const proposalAfterAdd = await mutateAsync(parsed.data);

      // O `itemId` do equipamento recém-criado só existe DEPOIS do POST — é
      // ele que endereça o PUT das coberturas. Descobrimos por diferença
      // contra os itens que a proposta já tinha; o fallback para o último
      // item cobre uma resposta em que a diferença não isole nada.
      const equipmentAfterAdd = proposalAfterAdd.equipment ?? [];
      const createdItem =
        equipmentAfterAdd.find(
          equipment => !existingItemIds.includes(equipment.itemId)
        ) ?? equipmentAfterAdd[equipmentAfterAdd.length - 1];

      let coverageError: string | null = null;
      if (createdItem && coverageRows.length > 0) {
        try {
          const response = await rdEquipamentosService.setCoverages(
            proposalId,
            createdItem.itemId,
            buildPayload(coverageRows)
          );
          if (!response.success || !response.data) {
            throw new Error(response.error || 'Erro ao salvar coberturas');
          }
        } catch (error) {
          // O equipamento JÁ foi criado — nunca desfazer/remover aqui (ver
          // `COVERAGE_RECOVERY_HINT`).
          coverageError =
            error instanceof ValidationError
              ? error.detail?.trim() ||
                (error.messages.length
                  ? error.messages.join('\n')
                  : error.message)
              : error instanceof Error
                ? error.message
                : 'Erro inesperado';
        }
      }

      // Coberturas alteram o prêmio: a `['rd-quote']` precisa cair junto com
      // a proposta, senão o Step Cotação mostra o valor antigo.
      await refetchRdProposalAndQuote(queryClient, proposalId);
      form.reset();
      setSubmitAttempted(false);
      onAdded?.();

      if (coverageError) {
        const description = `${COVERAGE_RECOVERY_HINT} (${coverageError})`;
        setSubmitError(description);
        toaster.create({
          type: 'error',
          title: 'Não foi possível salvar as coberturas',
          description,
        });
      }
    } catch (error) {
      if (error instanceof ValidationError && error.fieldErrors) {
        Object.entries(error.fieldErrors).forEach(([field, fieldError]) => {
          const fieldKey = field as keyof StepEquipamentoFormValues;
          // Mesma guarda de `clearSubmitErrors` acima — nunca chama
          // `setFieldMeta` num campo desmontado (ex.: `nfDate` quando
          // "Zero km" está desmarcado). Erros em campos desmontados já
          // são comunicados pelo toaster abaixo.
          if (form.getFieldMeta(fieldKey) === undefined) return;
          try {
            form.setFieldMeta(fieldKey, meta => ({
              ...meta,
              errorMap: { ...meta?.errorMap, onSubmit: fieldError.message },
            }));
          } catch {
            // Nunca deixa um erro de mapeamento de campo derrubar o
            // fluxo de submit — o toaster abaixo já comunica o erro.
          }
        });
      }

      const description =
        error instanceof ValidationError
          ? error.detail?.trim() ||
            (error.messages.length ? error.messages.join('\n') : error.message)
          : error instanceof Error
            ? error.message
            : 'Erro inesperado';

      setSubmitError(description);

      toaster.create({
        type: 'error',
        title: 'Não foi possível adicionar o equipamento',
        description,
      });
    }
  }

  return { handleSubmit, isPending, submitAttempted, submitError };
}
